import { RevxRenderFinishSize } from 'components/RevxRenderFinishSize';
import { MeasurementUnitEnum } from 'domains/measurementDomain';
import { OrientationEnum } from 'domains/skuDomain';
import { useMeasurementUnit } from 'hooks/useMeasurementUnit';
import './index.less';

interface Props {
  size: string | undefined;
  /** Metric twin of `size`, sent alongside so a unit switch needs no refetch. */
  sizeMetric?: string;
  /** The ready-made size this job was configured with, e.g. `A4`. */
  sizePreset?: string;
  /** Which catalogue that name came from. */
  sizePresetUnit?: MeasurementUnitEnum;
  orientation: string | undefined;
  /** Tightens the spacing, for the places that render inside a card line. */
  isWorkspace?: boolean;
}

/**
 * A job's finished size, named when it came from the ready-made list.
 *
 * One component rather than the same block in each of the twenty-odd places a job's size is
 * shown - workspace cards, the cart, order history, the quote, the summary, the adjustment
 * screens. They differed only in indentation, and the rule for when the name may be shown is
 * exactly the kind of thing that ends up applied in nineteen places out of twenty.
 *
 * The name is shown only to a reader working in the unit it was picked in. "A4" beside
 * 8.27x11.69in is a size nobody asks for by that name, so an imperial reader gets the dimensions.
 */
export const RevxJobSize = ({
  size,
  sizeMetric,
  sizePreset,
  sizePresetUnit,
  orientation,
  isWorkspace,
}: Props) => {
  const { displaySize, unit } = useMeasurementUnit();
  const isCube =
    (orientation ?? '').toLocaleLowerCase() === OrientationEnum.cube;

  return (
    <>
      {sizePreset && sizePresetUnit === unit && (
        <span className="revx-job-size-preset-name">{sizePreset}</span>
      )}
      {isCube ? (
        <RevxRenderFinishSize
          isWorkspace={isWorkspace}
          finishSize={size}
          finishSizeMetric={sizeMetric}
          orientation={
            (orientation ?? '').toLocaleLowerCase() as OrientationEnum
          }
        />
      ) : (
        <>{displaySize(size, { metric: sizeMetric })}</>
      )}
    </>
  );
};
